'use client';

import { Div, checkIsMobile, cn } from '@scatterlab/zeta-core';
import {
  FC,
  memo,
  useCallback,
  useEffect,
  MouseEvent as ReactMouseEvent,
  useMemo,
  useRef,
} from 'react';

export interface SliderProps {
  value: number;
  onValueChange: (value: number) => void;
  minimumValue?: number;
  maximumValue?: number;
  step?: number;
  className?: string;
}

const Slider: FC<SliderProps> = ({
  value,
  className,
  minimumValue = 0,
  maximumValue = 1,
  onValueChange,
  step = 0.01,
}) => {
  const sliderRef = useRef<HTMLDivElement>(null);
  const valueDivRef = useRef<HTMLDivElement>(null);
  const steppedList = useMemo(() => {
    const list = [];
    for (
      let i = minimumValue;
      i <= maximumValue;
      i = Math.round((i + step) * 100) / 100
    ) {
      list.push(i);
    }
    return list;
  }, [minimumValue, maximumValue, step]);

  useEffect(() => {
    if (!sliderRef.current || !valueDivRef.current) return;
    const maxWidth = sliderRef.current.clientWidth - 4;
    valueDivRef.current.style.width = `${((value - minimumValue) / (maximumValue - minimumValue)) * maxWidth + 4}px`;
  }, [value, maximumValue, minimumValue]);

  const handlePointerDown = useCallback(
    (e: ReactMouseEvent) => {
      if (!sliderRef.current || !valueDivRef.current) return;
      const initialX = e.clientX;
      const initialWidth = valueDivRef.current.clientWidth;
      const maxWidth = sliderRef.current.clientWidth - 4;

      const handleDrag = (ev: MouseEvent) => {
        const newWidth = Math.min(
          maxWidth,
          Math.max(0, initialWidth + ev.clientX - initialX),
        );
        const oldValue =
          (valueDivRef.current!.clientWidth / maxWidth) *
          (maximumValue - minimumValue);
        const steppedOldValue =
          Math.round(
            (Math.round(oldValue / step) * step + minimumValue) * 100,
          ) / 100;
        const newValue = (newWidth / maxWidth) * (maximumValue - minimumValue);
        const steppedValue =
          Math.round(
            (Math.round(newValue / step) * step + minimumValue) * 100,
          ) / 100;
        if (steppedOldValue !== steppedValue) {
          valueDivRef.current!.style.width = `${
            ((steppedValue - minimumValue) / (maximumValue - minimumValue)) *
              maxWidth +
            4
          }px`;
        }
      };

      const handleTouchMove = (ev: TouchEvent) => {
        const newWidth = Math.min(
          maxWidth,
          Math.max(0, initialWidth + ev.touches[0]!.clientX - initialX),
        );
        const oldValue =
          (valueDivRef.current!.clientWidth / maxWidth) *
          (maximumValue - minimumValue);
        const steppedOldValue =
          Math.round(
            (Math.round(oldValue / step) * step + minimumValue) * 100,
          ) / 100;
        const newValue = (newWidth / maxWidth) * (maximumValue - minimumValue);
        const steppedValue =
          Math.round(
            (Math.round(newValue / step) * step + minimumValue) * 100,
          ) / 100;
        if (steppedOldValue !== steppedValue) {
          valueDivRef.current!.style.width = `${
            ((steppedValue - minimumValue) / (maximumValue - minimumValue)) *
              maxWidth +
            4
          }px`;
        }
      };

      const handleDragEnd = () => {
        if (checkIsMobile()) {
          window.removeEventListener('touchmove', handleTouchMove);
          window.removeEventListener('touchend', handleDragEnd);
        } else {
          window.removeEventListener('pointermove', handleDrag);
          window.removeEventListener('pointerup', handleDragEnd);
        }

        const newValue =
          (valueDivRef.current!.clientWidth / maxWidth) *
          (maximumValue - minimumValue);
        const steppedValue =
          Math.round(
            (Math.round(newValue / step) * step + minimumValue) * 100,
          ) / 100;
        if (value !== steppedValue) onValueChange(steppedValue);
      };

      if (checkIsMobile()) {
        window.addEventListener('touchmove', handleTouchMove);
        window.addEventListener('touchend', handleDragEnd);
      } else {
        window.addEventListener('pointermove', handleDrag);
        window.addEventListener('pointerup', handleDragEnd);
      }
    },
    [maximumValue, minimumValue, onValueChange, step, value],
  );

  const handlePress = useCallback(
    (e: ReactMouseEvent) => {
      e.preventDefault();
      if (!sliderRef.current || !valueDivRef.current) return;
      const { x: initX, width: maxWidth } =
        sliderRef.current.getBoundingClientRect();
      const x = e.clientX - initX;
      const newValue = (x / maxWidth) * (maximumValue - minimumValue);
      const steppedValue =
        Math.round((Math.round(newValue / step) * step + minimumValue) * 100) /
        100;
      if (value !== steppedValue) onValueChange(steppedValue);
    },
    [value, maximumValue, minimumValue, step, onValueChange],
  );

  return (
    <Div
      className={cn('flex h-7 cursor-pointer py-3', className)}
      onPointerDown={handlePress}
    >
      <Div
        className="relative mx-2 h-1 flex-1 rounded-full bg-white/[8%]"
        ref={sliderRef}
      >
        <Div className="flex h-1 flex-row justify-between">
          {steppedList.map((steppedValue) => (
            <Div
              className="size-1 rounded-full bg-white/[8%]"
              key={steppedValue}
            />
          ))}
        </Div>
        <Div
          ref={valueDivRef}
          className="absolute -top-px left-0 h-1.5 rounded-full bg-primary-400"
        >
          <Div
            className="absolute -right-2 top-[-7px] size-5 rounded-full bg-white"
            onPointerDown={handlePointerDown}
          />
        </Div>
      </Div>
    </Div>
  );
};

export default memo(Slider);
